import React, { useEffect, useState } from 'react';
import { Alert, Avatar, Box, Button, Card, Divider, Input, Snackbar, Stack, Typography } from '@mui/joy';
import { BarChart, Report, Timeline, TrendingUp, TrendingDown, DragHandle, Info } from '@mui/icons-material';
import { ApexOptions } from 'apexcharts';

import LoadingSpinnerComponent from '../components/LoadingSpinnerComponent';
import ReactApexChart from 'react-apexcharts';
import { useAuth } from '../context/authContext';
import { cardStyles } from '../styles/cardStyles';
import InfoTooltipComponent from '../components/InfoTooltipComponent';
import { exportToCSV, transformDailyAverageRatingDataForCSV, transformDailyDeltaRatingDataForCSV } from '../Helpers/CSVExportHelper';
import { calculateTrendDirection, calculateTrendPercentage } from '../Helpers/TrendlineHelper';

const networking = require('../Networking/Ratings/RatingsAPI');
const helper = require('../Helpers/helpers');

type RatingRow = {
    id: string
    app: string
    apple_rating: string | null
    google_rating: string | null
    date: string
    delta_apple_count_1star: number
    delta_apple_count_2star: number
    delta_apple_count_3star: number
    delta_apple_count_4star: number
    delta_apple_count_5star: number
    apple_daily_average: number | null
    delta_google_count_1star: number
    delta_google_count_2star: number
    delta_google_count_3star: number
    delta_google_count_4star: number
    delta_google_count_5star: number
    google_daily_average: number | null
}

function RatingsPage({ app, subscription }: any) {
    const { token } = useAuth();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showError, setShowError] = useState(false);
    const [data, setData] = useState<RatingRow[]>([]);
    const [exporting, setExporting] = useState(false);
    const [fromDate, setFromDate] = useState(new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0]);
    const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);

    useEffect(() => {
        getRatingsData();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromDate, toDate]);

    function getRatingsData() {
        setLoading(true);
        const { from, to } = { from: fromDate, to: toDate };

        networking.getRatingHistory(app.id, token, from, to).then((data: any) => {
            setData(data.data.history);
            setLoading(false);
        }).catch((error: Error) => {
            setError(error.message);
            setShowError(true);
            setLoading(false);
        });
    }

    function handleDailyAverageExportToCSV() {
        try {
            setExporting(true);
            const filename = `daily_average_rating_${app.name}_${new Date().toLocaleDateString()}`;
            const transformedData = transformDailyAverageRatingDataForCSV(grouped);
            exportToCSV(transformedData, filename);
        } catch (err) {
            setError('Failed to export CSV');
            setShowError(true);
        } finally {
            setExporting(false);
        }
    }

    function handleDailyDeltaExportToCSV() {
        try {
            setExporting(true);
            const filename = `daily_delta_rating_${app.name}_${new Date().toLocaleDateString()}`;
            const transformedData = transformDailyDeltaRatingDataForCSV(grouped);
            exportToCSV(transformedData, filename);
        } catch (err) {
            setError('Failed to export CSV');
            setShowError(true);
        } finally {
            setExporting(false);
        }
    }

    const justtAppleRatingsNotNull = data.map((entry: any) => entry.apple_rating).filter((rating: number) => rating !== null);
    const justGoogleRatingsNotNull = data.map((entry: any) => entry.google_rating).filter((rating: number) => rating !== null);

    //calculate average ratings
    const validAppleRatings = justtAppleRatingsNotNull
        .map(Number)
        .filter(n => Number.isFinite(n));

    const averageAppleRating = validAppleRatings.length > 0
        ? (validAppleRatings.reduce((acc: number, curr: number) => acc + curr, 0) / validAppleRatings.length).toFixed(2)
        : "0.00";

    const validGoogleRatings = justGoogleRatingsNotNull
        .map(Number)
        .filter(n => Number.isFinite(n));

    const averageGoogleRating = validGoogleRatings.length > 0
        ? (validGoogleRatings.reduce((acc: number, curr: number) => acc + curr, 0) / validGoogleRatings.length).toFixed(2)
        : "0.00";

    const grouped = new Map<string, { apple?: number, google?: number, googleDeltas?: number[], appleDeltas?: number[], averageApple?: number, averageGoogle?: number }>()

    data.forEach(item => {
        const date = new Date(item.date).toISOString().split("T")[0]

        if (!grouped.has(date)) {
            grouped.set(date, {})
        }

        const entry = grouped.get(date)!

        if (item.apple_rating) {
            entry.apple = Number(item.apple_rating)
        }

        entry.appleDeltas = [
            item.delta_apple_count_1star,
            item.delta_apple_count_2star,
            item.delta_apple_count_3star,
            item.delta_apple_count_4star,
            item.delta_apple_count_5star
        ]

        entry.averageApple = item.apple_daily_average ? Number(item.apple_daily_average) : undefined

        if (item.google_rating) {
            entry.google = Number(item.google_rating)
        }

        entry.googleDeltas = [
            item.delta_google_count_1star,
            item.delta_google_count_2star,
            item.delta_google_count_3star,
            item.delta_google_count_4star,
            item.delta_google_count_5star
        ]

        entry.averageGoogle = item.google_daily_average ? Number(item.google_daily_average) : undefined
    })

    const dates = Array.from(grouped.keys()).sort()

    // Filter the deltas tha have more than 0 to avoid showing them in the chart, 
    // since they can be very noisy and not very informative
    const appleDeltas = dates.map(d => grouped.get(d)?.appleDeltas?.map(delta => delta > 0 ? delta : 0) ?? null)
    const googleDeltas = dates.map(d => grouped.get(d)?.googleDeltas?.map(delta => delta > 0 ? delta : 0) ?? null)

    // Calculate trend lines (keep null for accurate trend calculations)
    const appleAverageDataForTrend = dates.map(d => grouped.get(d)?.averageApple ?? null);
    const googleAverageDataForTrend = app.androidpackageid ? dates.map(d => grouped.get(d)?.averageGoogle ?? null) : [];

    // Convert to 0 for chart display
    const appleAverageData = appleAverageDataForTrend.map(v => v ?? 0);
    const googleAverageData = googleAverageDataForTrend.map(v => v ?? 0);

    // Calculate trend directions (using null values to exclude missing data)
    const appleTrendDirection = calculateTrendDirection(appleAverageDataForTrend);
    const googleTrendDirection = calculateTrendDirection(googleAverageDataForTrend);
    const appleTrendPercentage = calculateTrendPercentage(appleAverageDataForTrend);
    const googleTrendPercentage = calculateTrendPercentage(googleAverageDataForTrend);

    const seriesDailyAverage = [
        {
            name: "Apple",
            data: appleAverageData
        },
        {
            name: "Google",
            data: app.androidpackageid ? googleAverageData : []
        }
    ]

    const options: ApexOptions = {
        chart: {
            type: "line",
            height: 250,
            toolbar: { show: false },
            zoom: { enabled: false }
        },
        dataLabels: {
            formatter: function (val: number) {
                return val ? val.toFixed(2) : "0.00";
            }
        },
        stroke: {
            width: [2, 2, 2, 2],
            dashArray: [0, 0, 5, 5]
        },
        colors: ["#008FFB", "#34A853"],
        grid: {
            borderColor: "#E5E7EB",
            strokeDashArray: 3
        },
        yaxis: {
            min: 0,
            max: 5,
            tickAmount: 4,
            labels: {
                formatter: v => v ? v.toFixed(2) : "0.00",
                style: {
                    colors: "#6B7280",
                    fontSize: "12px"
                }
            }
        },
        xaxis: {
            categories: dates,
            labels: {
                formatter: function (val: string) {
                    const date = new Date(val);
                    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                },
                style: {
                    colors: "#6B7280",
                    fontSize: "11px"
                }
            },
            axisBorder: { show: false },
            axisTicks: { show: false }
        },
        legend: {
            position: "bottom",
            labels: {
                colors: "#374151"
            },
        },
        tooltip: {
            theme: "light"
        }
    }

    const series2 = [
        {
            name: 'Apple ⭐️',
            group: 'apple',
            color: '#80C7FD',
            data: appleDeltas.map(d => d ? d[0] : 0),
        },
        {
            name: 'Google ⭐️',
            group: 'google',
            color: '#D7F2E1',
            data: googleDeltas.map(d => d ? d[0] : 0),
        },
        {
            name: 'Apple ⭐️⭐️',
            group: 'apple',
            color: '#66BAFC',
            data: appleDeltas.map(d => d ? d[1] : 0),
        },
        {
            name: 'Google ⭐️⭐️',
            group: 'google',
            color: '#A8E0BF',
            data: googleDeltas.map(d => d ? d[1] : 0),
        },
        {
            name: 'Apple ⭐️⭐️⭐️',
            group: 'apple',
            color: '#4DADFC',
            data: appleDeltas.map(d => d ? d[2] : 0),
        },
        {
            name: 'Google ⭐️⭐️⭐️',
            group: 'google',
            color: '#73CB96',
            data: googleDeltas.map(d => d ? d[2] : 0),
        },
        {
            name: 'Apple ⭐️⭐️⭐️⭐️',
            group: 'apple',
            color: '#269BFB',
            data: appleDeltas.map(d => d ? d[3] : 0),
        },
        {
            name: 'Google ⭐️⭐️⭐️⭐️',
            group: 'google',
            color: '#4AB56C',
            data: googleDeltas.map(d => d ? d[3] : 0),
        },
        {
            name: 'Apple ⭐️⭐️⭐️⭐️⭐️',
            group: 'apple',
            color: '#008FFB',
            data: appleDeltas.map(d => d ? d[4] : 0),
        },
        {
            name: 'Google ⭐️⭐️⭐️⭐️⭐️',
            group: 'google',
            color: '#34A853',
            data: googleDeltas.map(d => d ? d[4] : 0),
        },
    ];

    const options2: ApexOptions = {
        chart: {
            type: 'bar',
            height: 250,
            stacked: true,
            stackType: '100%',
            toolbar: { show: false }
        },
        grid: {
            borderColor: "#E5E7EB",
            strokeDashArray: 3
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '100%',
                borderRadius: 6,
                borderRadiusApplication: 'end',
                borderRadiusWhenStacked: 'last'
            },
        },
        fill: {
            opacity: 0.9,
            type: 'solid'
        },
        stroke: {
            show: true,
            width: 2,
            colors: ['#ffffff']
        },
        dataLabels: {
            enabled: false,
        },
        xaxis: {
            categories: dates,
            labels: {
                formatter: function (val: string) {
                    const date = new Date(val);
                    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                },
                style: {
                    colors: "#6B7280",
                    fontSize: "12px"
                }
            },
            axisBorder: { show: false },
            axisTicks: { show: false }
        },
        yaxis: {
            labels: {
                style: {
                    colors: "#6B7280",
                    fontSize: "12px"
                }
            }
        },
        tooltip: {
            theme: "light"
        }
    };

    return (
        <div>
            {showError &&
                <Snackbar
                    variant="soft"
                    color="danger"
                    open={showError}
                    onClose={() => setShowError(false)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    startDecorator={<Report />}
                    endDecorator={
                        <Button
                            onClick={() => setShowError(false)}
                            size="sm"
                            variant="soft"
                            color="danger"
                        >
                            Dismiss
                        </Button>
                    }
                >
                    {error}
                </Snackbar>
            }
            <Alert
                className="mb-3"
                size="lg"
                variant="soft"
                color="warning"
                sx={{
                    borderColor: '#fbbf24',
                    borderWidth: '1px',
                }}>
                <div>
                    <Typography
                        level="body-xs">
                        <Info sx={{ fontSize: '16px', marginRight: '8px' }} />
                        Ratings data is based on the information received and processed by our system,
                        and there may be some delay in processing new ratings.
                        Therefore, the ratings data may be different from the app stores, especially for recent ratings.
                    </Typography>
                </div>
            </Alert>
            <Box
                component='section'
                color='primary'
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginBottom: 2,
                    marginTop: 4
                }}>
                <div className='row gx-3'>
                    <div className='col-auto my-auto iron legend bold'>
                        <Typography
                            sx={{
                                marginTop: 'auto',
                                fontSize: '12px',
                                fontWeight: 600
                            }}>
                            from
                        </Typography>
                    </div>
                    <div className='col-auto'>
                        <Input
                            type='date'
                            slotProps={{
                                input: {
                                    min: new Date(new Date().setDate(new Date().getDate() - 120)).toISOString().split('T')[0],
                                    max: toDate,
                                },
                            }}
                            defaultValue={fromDate}
                            onChange={(event) => {
                                setFromDate(event.target.value);
                            }}
                            sx={{
                                borderRadius: '10px',
                                padding: '0 12px',
                                height: '30px',
                                backgroundColor: '#ffffff',
                                fontSize: '12px',
                            }} />
                    </div>
                    <div className='col-auto my-auto iron legend bold'>
                        <Typography
                            sx={{
                                marginTop: 'auto',
                                fontSize: '12px',
                                fontWeight: 600
                            }}>
                            to
                        </Typography>
                    </div>
                    <div className='col-auto'>
                        <Input
                            type='date'
                            slotProps={{
                                input: {
                                    min: fromDate,
                                    max: toDate
                                },
                            }}
                            defaultValue={toDate}
                            onChange={(event) => {
                                setToDate(event.target.value);
                            }}
                            sx={{
                                borderRadius: '10px',
                                padding: '0 12px',
                                height: '30px',
                                backgroundColor: '#ffffff',
                                fontSize: '12px',
                            }} />
                    </div>
                </div>
            </Box>

            {loading ?
                <LoadingSpinnerComponent />
                :
                <>
                    {data &&
                        <>
                            <div className='row'>
                                {/* Average ratings cards */}
                                {app.appleappid &&
                                    <div className='col-md mb-3'>
                                        <Card
                                            variant='outlined'
                                            sx={{
                                                ...cardStyles.base
                                            }}>
                                            <div className='col-md my-auto'>
                                                <Stack
                                                    spacing={1}
                                                    sx={{
                                                        textAlign: 'center'
                                                    }}>
                                                    <div className='row text-start'>
                                                        <div className='col-md-auto my-auto iron legend bold'>
                                                            <Avatar>
                                                                {helper.iconFor('applestore')}
                                                            </Avatar>
                                                        </div>
                                                        <div className='col-md-auto my-auto iron legend bold'>
                                                            <Typography
                                                                color='neutral'
                                                                fontWeight={600}
                                                                level='body-xs'>
                                                                {"Average rating".toUpperCase()}
                                                                <InfoTooltipComponent
                                                                    message={"It shows the average rating value (from 1 to 5 stars) based on the ratings received."}
                                                                    size={'sm'}
                                                                    variant={'plain'}
                                                                    color={'primary'} />
                                                            </Typography>
                                                            <div className='row'>
                                                                <Typography
                                                                    color='primary'
                                                                    fontWeight={300}
                                                                    sx={{
                                                                        fontSize: 32
                                                                    }}
                                                                    className='col-md-auto'>
                                                                    {averageAppleRating}
                                                                </Typography>
                                                                <Stack
                                                                    direction="row"
                                                                    spacing={0.5}
                                                                    alignItems="center"
                                                                    className='col-md-auto'>
                                                                    {appleTrendDirection === 'up' && (
                                                                        <>
                                                                            <TrendingUp sx={{ fontSize: '1.1rem', color: '#34A853' }} />
                                                                            <Typography level="body-xs" sx={{ color: '#34A853' }}>
                                                                                {Math.abs(appleTrendPercentage).toFixed(0)}%
                                                                            </Typography>
                                                                        </>
                                                                    )}
                                                                    {appleTrendDirection === 'down' && (
                                                                        <>
                                                                            <TrendingDown sx={{ fontSize: '1.1rem', color: '#DC2626' }} />
                                                                            <Typography level="body-xs" sx={{ color: '#DC2626' }}>
                                                                                {Math.abs(appleTrendPercentage).toFixed(0)}%
                                                                            </Typography>
                                                                        </>
                                                                    )}
                                                                    {appleTrendDirection === 'stable' && (
                                                                        <Typography level="body-xs" sx={{ color: '#6B7280' }}>
                                                                            <DragHandle sx={{ fontSize: '1.1rem', color: '#6B7280' }} />
                                                                        </Typography>
                                                                    )}
                                                                </Stack>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </Stack>
                                            </div>
                                        </Card>
                                    </div>
                                }
                                {app.androidpackageid &&
                                    <div className='col-md mb-3'>
                                        <Card
                                            variant='outlined'
                                            sx={{
                                                ...cardStyles.base
                                            }}>
                                            <div className='col-md my-auto'>
                                                <Stack
                                                    spacing={1}
                                                    sx={{
                                                        textAlign: 'center'
                                                    }}>
                                                    <div className='row text-start'>
                                                        <div className='col-md-auto my-auto iron legend bold'>
                                                            <Avatar>
                                                                {helper.iconFor('googlestore')}
                                                            </Avatar>
                                                        </div>
                                                        <div className='col-md-auto my-auto iron legend bold'>
                                                            <Typography
                                                                color='neutral'
                                                                fontWeight={600}
                                                                level='body-xs'>
                                                                {"Average rating".toUpperCase()}
                                                                <InfoTooltipComponent
                                                                    message={"It shows the average rating value (from 1 to 5 stars) based on the ratings received."}
                                                                    size={'sm'}
                                                                    variant={'plain'}
                                                                    color={'primary'} />
                                                            </Typography>
                                                            <div className='row'>
                                                                <Typography
                                                                    color='primary'
                                                                    fontWeight={300}
                                                                    sx={{
                                                                        fontSize: 32
                                                                    }}
                                                                    className='col-md-auto'>
                                                                    {averageGoogleRating}
                                                                </Typography>
                                                                <Stack
                                                                    direction="row"
                                                                    spacing={0.5}
                                                                    alignItems="center"
                                                                    className='col-md-auto'>
                                                                    {googleTrendDirection === 'up' && (
                                                                        <>
                                                                            <TrendingUp sx={{ fontSize: '1.1rem', color: '#34A853' }} />
                                                                            <Typography level="body-xs" sx={{ color: '#34A853' }}>
                                                                                {Math.abs(googleTrendPercentage).toFixed(0)}%
                                                                            </Typography>
                                                                        </>
                                                                    )}
                                                                    {googleTrendDirection === 'down' && (
                                                                        <>
                                                                            <TrendingDown sx={{ fontSize: '1.1rem', color: '#DC2626' }} />
                                                                            <Typography level="body-xs" sx={{ color: '#DC2626' }}>
                                                                                {Math.abs(googleTrendPercentage).toFixed(0)}%
                                                                            </Typography>
                                                                        </>
                                                                    )}
                                                                    {googleTrendDirection === 'stable' && (
                                                                        <Typography level="body-xs" sx={{ color: '#6B7280' }}>
                                                                            <DragHandle sx={{ fontSize: '1.1rem', color: '#6B7280' }} />
                                                                        </Typography>
                                                                    )}
                                                                </Stack>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </Stack>
                                            </div>
                                        </Card>
                                    </div>
                                }
                            </div>
                            <div className='row p-0'>
                                <div className='col-md mb-3'>
                                    <Card
                                        variant='outlined'
                                        sx={{
                                            ...cardStyles.base
                                        }}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Timeline sx={{ color: '#f97316', fontSize: '1.1rem' }} />
                                            <Typography
                                                color='neutral'
                                                fontWeight={600}
                                                level='body-xs'>
                                                {"Daily average rating".toUpperCase()}
                                                <InfoTooltipComponent
                                                    message={"The daily average rating timeline shows the average rating based on the ratings received on each day. This can be useful to identify trends and patterns in the ratings, as it smooths out the noise of individual ratings and shows their overall direction."}
                                                    size={'sm'}
                                                    variant={'plain'}
                                                    color={'primary'} />
                                            </Typography>
                                            {subscription && subscription.plan !== 'A' &&
                                                <div className='col ms-auto'>
                                                    <div className='text-end'>
                                                        <Button
                                                            variant="soft"
                                                            size='sm'
                                                            color="neutral"
                                                            onClick={handleDailyAverageExportToCSV}
                                                            loading={exporting}
                                                            disabled={exporting || loading || data.length === 0}>
                                                            Export to csv
                                                        </Button>
                                                    </div>
                                                </div>
                                            }
                                        </Stack>
                                        <Divider sx={{ mb: 2 }} />
                                        <ReactApexChart
                                            options={options}
                                            series={seriesDailyAverage}
                                            type="line"
                                            width={'96%'}
                                            height={250}
                                        />
                                    </Card>
                                </div>
                            </div>
                            <div className='row p-0'>
                                <div className='col-md mb-3'>
                                    <Card
                                        variant='outlined'
                                        sx={{
                                            ...cardStyles.base
                                        }}>
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                            <BarChart sx={{ color: '#f97316', fontSize: '1.1rem' }} />
                                            <Typography
                                                color='neutral'
                                                fontWeight={600}
                                                level='body-xs'>
                                                {"Daily ratings".toUpperCase()}
                                                <InfoTooltipComponent
                                                    message={"The daily ratings chart shows the number of ratings received on each day, broken down by rating value (1-5 stars). This can be useful to identify trends and patterns in the ratings, as it shows how many ratings were received on each day and how they were distributed across the different rating values."}
                                                    size={'sm'}
                                                    variant={'plain'}
                                                    color={'primary'} />
                                            </Typography>
                                            {subscription && subscription.plan !== 'A' &&
                                                <div className='col ms-auto'>
                                                    <div className='text-end'>
                                                        <Button
                                                            variant="soft"
                                                            size='sm'
                                                            color="neutral"
                                                            onClick={handleDailyDeltaExportToCSV}
                                                            loading={exporting}
                                                            disabled={exporting || loading || data.length === 0}>
                                                            Export to csv
                                                        </Button>
                                                    </div>
                                                </div>
                                            }
                                        </Stack>
                                        <Divider sx={{ mb: 2 }} />
                                        <ReactApexChart
                                            options={options2}
                                            series={series2}
                                            type="bar"
                                            height={450}
                                        />
                                    </Card>
                                </div>
                            </div>
                        </>
                    }
                </>
            }
        </div >
    );
}

export default RatingsPage;